import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { CheckCircle, Calculator, DollarSign, Plus, Trash2, TrendingUp, ArrowRight, Zap, Search, Info } from "lucide-react";
import { CPT_LOOKUP, searchCptCodes } from "../lib/cptLookup";
import SharedNavbar from "../components/SharedNavbar";

const APP_URL = "";

// ── CPT Row Component ──────────────────────────────────────────────────────────
function CptRow({ row, index, onUpdate, onRemove, canRemove }) {
  const [query, setQuery] = useState(row.cpt);
  const [results, setResults] = useState([]);
  const [showDrop, setShowDrop] = useState(false);
  const dropRef = useRef(null);

  const search = useCallback((q) => {
    if (!q || q.trim().length < 2) { setResults([]); setShowDrop(false); return; }
    const out = searchCptCodes(q, 8).map(r => ({
      code: r.code,
      description: r.name || r.code,
      wrvu: r.wrvu || 0,
    }));
    setResults(out);
    setShowDrop(out.length > 0);
  }, []);

  const handleCodeChange = (val) => {
    setQuery(val);
    onUpdate(index, { cpt: val, description: "", wrvu: null });
    search(val);
  };

  const handleSelect = (item) => {
    setQuery(item.code);
    setShowDrop(false);
    onUpdate(index, { cpt: item.code, description: item.description, wrvu: item.wrvu });
  };

  useEffect(() => {
    const handler = (e) => { if (dropRef.current && !dropRef.current.contains(e.target)) setShowDrop(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const rowWrvu = row.wrvu && row.units ? (row.wrvu * parseFloat(row.units || 0)).toFixed(2) : null;

  return (
    <div className="grid grid-cols-12 gap-2 items-start">
      {/* CPT code input */}
      <div className="col-span-4 relative" ref={dropRef}>
        <input
          type="text"
          value={query}
          onChange={(e) => handleCodeChange(e.target.value)}
          placeholder="Code or keywords"
          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400"
        />
        {showDrop && (
          <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-xl max-h-52 overflow-y-auto" style={{ minWidth: "280px" }}>
            {results.map((r) => (
              <button
                key={r.code}
                onMouseDown={() => handleSelect(r)}
                className="w-full text-left px-3 py-2 hover:bg-blue-50 transition-colors border-b border-slate-50 last:border-0"
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-bold text-blue-700">{r.code}</span>
                  <span className="text-xs font-semibold text-green-600">{r.wrvu} wRVU</span>
                </div>
                <p className="text-xs text-slate-500 truncate mt-0.5">{r.description.substring(0, 65)}</p>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Units */}
      <div className="col-span-3">
        <input
          type="number"
          min="0"
          value={row.units}
          onChange={(e) => onUpdate(index, { units: e.target.value })}
          placeholder="# visits"
          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400"
        />
      </div>

      {/* wRVU per unit */}
      <div className="col-span-2">
        <div className={`px-3 py-2 text-sm rounded-lg text-center font-semibold ${row.wrvu ? "bg-green-50 text-green-700 border border-green-200" : "bg-slate-50 text-slate-400 border border-slate-100"}`}>
          {row.wrvu ?? "—"}
        </div>
      </div>

      {/* Row total */}
      <div className="col-span-2">
        <div className={`px-3 py-2 text-sm rounded-lg text-center font-bold ${rowWrvu ? "bg-blue-50 text-blue-700 border border-blue-200" : "bg-slate-50 text-slate-400 border border-slate-100"}`}>
          {rowWrvu ?? "—"}
        </div>
      </div>

      {/* Remove */}
      <div className="col-span-1 flex justify-center">
        {canRemove && (
          <button onClick={() => onRemove(index)} className="text-slate-400 hover:text-red-500 transition-colors mt-2">
            <Trash2 size={14} />
          </button>
        )}
      </div>
    </div>
  );
}

// ── Common CPT Reference Data ──────────────────────────────────────────────────
const CPT_REFERENCE = {
  "Primary Care": [
    { code: "99395", desc: "Established — Preventive, 18–39 yrs", wrvu: 1.50 },
    { code: "99396", desc: "Established — Preventive, 40–64 yrs", wrvu: 1.74 },
    { code: "99397", desc: "Established — Preventive, 65+ yrs", wrvu: 1.85 },
    { code: "99385", desc: "New Patient — Preventive, 18–39 yrs", wrvu: 1.92 },
    { code: "99386", desc: "New Patient — Preventive, 40–64 yrs", wrvu: 2.33 },
    { code: "99387", desc: "New Patient — Preventive, 65+ yrs", wrvu: 2.46 },
  ],
  "Urgent Care": [
    { code: "99203", desc: "New Patient — Low Complexity", wrvu: 1.60 },
    { code: "99204", desc: "New Patient — Moderate Complexity", wrvu: 2.60 },
    { code: "99205", desc: "New Patient — High Complexity", wrvu: 3.50 },
    { code: "99213", desc: "Est. Patient — Low Complexity", wrvu: 1.30 },
    { code: "99214", desc: "Est. Patient — Moderate Complexity", wrvu: 1.92 },
    { code: "99215", desc: "Est. Patient — High Complexity", wrvu: 2.80 },
  ],
  "Emergency Department": [
    { code: "99281", desc: "ED Level 1 — Self-limited / Minor", wrvu: 0.45 },
    { code: "99282", desc: "ED Level 2 — Low Complexity", wrvu: 0.88 },
    { code: "99283", desc: "ED Level 3 — Moderate Complexity", wrvu: 1.60 },
    { code: "99284", desc: "ED Level 4 — High Complexity", wrvu: 2.74 },
    { code: "99285", desc: "ED Level 5 — Very High Complexity", wrvu: 3.80 },
  ],
  "Common Procedures": [
    { code: "93000", desc: "EKG with interpretation", wrvu: 0.17 },
    { code: "71046", desc: "Chest X-ray, 2 views", wrvu: 0.22 },
    { code: "10060", desc: "I&D of abscess, simple", wrvu: 0.70 },
    { code: "12001", desc: "Simple laceration repair, ≤2.5 cm", wrvu: 0.81 },
    { code: "99211", desc: "Suture removal / minimal est. visit", wrvu: 0.18 },
  ],
};

// ── Main Page ──────────────────────────────────────────────────────────────────
export default function FreeWRVUCalculator() {
  useEffect(() => {
    const prevTitle = document.title;
    const metaDesc = document.querySelector('meta[name="description"]');
    const prevDesc = metaDesc ? metaDesc.getAttribute("content") : "";

    document.title = "Free RVU Calculator — The Fastest RVU Tracker. PERIOD. | Doc RVU Tracker";
    if (metaDesc) metaDesc.setAttribute("content", "Calculate physician, NP & PA RVUs instantly with our free RVU calculator. Fast, Simple, and Accurate.");

    return () => {
      document.title = prevTitle;
      if (metaDesc) metaDesc.setAttribute("content", prevDesc);
    };
  }, []);

  const [rows, setRows] = useState([
    { cpt: "", description: "", wrvu: null, units: "" },
    { cpt: "", description: "", wrvu: null, units: "" },
    { cpt: "", description: "", wrvu: null, units: "" },
    { cpt: "", description: "", wrvu: null, units: "" },
    { cpt: "", description: "", wrvu: null, units: "" },
  ]);
  const [conversionFactor, setConversionFactor] = useState("55");
  const [activeTab, setActiveTab] = useState("Primary Care");
  const [mgmaTab, setMgmaTab] = useState("Physician");

  const updateRow = (index, changes) => {
    setRows((prev) => prev.map((r, i) => i === index ? { ...r, ...changes } : r));
  };

  const addRow = () => {
    if (rows.length < 12) setRows((prev) => [...prev, { cpt: "", description: "", wrvu: null, units: "" }]);
  };

  const removeRow = (index) => {
    setRows((prev) => prev.filter((_, i) => i !== index));
  };

  const totals = useMemo(() => {
    let totalWRVU = 0;
    rows.forEach((r) => {
      if (r.wrvu != null && r.units && !isNaN(r.units) && parseFloat(r.units) > 0) {
        totalWRVU += r.wrvu * parseFloat(r.units);
      }
    });
    const cf = parseFloat(conversionFactor) || 0;
    return {
      totalWRVU: Math.round(totalWRVU * 100) / 100,
      earnings: Math.round(totalWRVU * cf).toLocaleString(),
    };
  }, [rows, conversionFactor]);

  return (
    <div className="min-h-screen font-sans antialiased" style={{ fontFamily: "Inter, -apple-system, BlinkMacSystemFont, sans-serif", background: "#f8fafc" }}>
      <SharedNavbar activePage="calculator" />

      {/* ── HERO ────────────────────────────────────────────────────────────── */}
      <section className="pt-28 pb-12 md:pt-36 md:pb-16 px-4" style={{ background: "linear-gradient(160deg, #0f172a 0%, #1e3a5f 60%, #0f172a 100%)" }}>
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-blue-500/20 border border-blue-500/30 rounded-full px-4 py-1.5 mb-6">
            <Calculator size={14} className="text-blue-400" />
            <span className="text-blue-300 text-xs font-semibold uppercase tracking-widest">Free Tool — No Account Required</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-4 leading-tight tracking-tight">
            Free{" "}
            <span style={{ background: "linear-gradient(135deg, #2563eb 0%, #0ea5e9 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
              wRVU Calculator
            </span>
          </h1>
          <p className="text-lg text-slate-300 max-w-2xl mx-auto mb-6">
            Calculate your work Relative Value Units instantly. Enter CPT codes, see wRVUs, and estimate your productivity earnings — built for physicians, NPs, and PAs.
          </p>
          <div className="flex flex-wrap justify-center gap-3 text-xs text-slate-400">
            {["11,000+ CPT codes", "Real CMS wRVU values", "Instant earnings estimate", "100% Free"].map((t) => (
              <span key={t} className="flex items-center gap-1.5 bg-white/5 border border-white/10 rounded-full px-3 py-1">
                <CheckCircle size={11} className="text-green-400" /> {t}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ── MAIN TWO-COLUMN ─────────────────────────────────────────────────── */}
      <div className="max-w-7xl mx-auto px-4 py-4 lg:py-6">
        <div className="flex flex-col lg:flex-row gap-10 lg:gap-14 items-start">

          {/* ── LEFT: Educational Content ───────────────────────────────────── */}
          <div className="w-full lg:w-3/5 space-y-12">

            {/* Intro */}
            <div>
              <p className="text-slate-600 text-base leading-relaxed">
                This free wRVU calculator provides instant analysis of work Relative Value Units for any CPT or HCPCS code. Enter a CPT code and the number of encounters, and the calculator returns your total wRVUs and an estimated productivity earnings value. wRVU values are based on the CMS Medicare Physician Fee Schedule (MPFS).
              </p>
            </div>

            {/* What is an RVU */}
            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-3 tracking-tight">What Is an RVU?</h2>
              <p className="text-slate-600 text-base leading-relaxed mb-4">
                A <strong>Relative Value Unit (RVU)</strong> is a measure used by Medicare and most commercial insurers to determine the reimbursement value of a medical service. The system is called the <strong>Resource-Based Relative Value Scale (RBRVS)</strong> and was established by CMS.
              </p>
              <p className="text-slate-600 text-base leading-relaxed mb-4">
                Every CPT code is assigned a total RVU value composed of three separate components:
              </p>
              <div className="overflow-x-auto rounded-xl border border-slate-200">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="text-left px-4 py-3 font-semibold text-slate-700">Component</th>
                      <th className="text-left px-4 py-3 font-semibold text-slate-700">Full Name</th>
                      <th className="text-left px-4 py-3 font-semibold text-slate-700">What It Covers</th>
                      <th className="text-left px-4 py-3 font-semibold text-slate-700">Used for Provider Pay?</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    <tr className="bg-green-50">
                      <td className="px-4 py-3 font-bold text-green-700">wRVU</td>
                      <td className="px-4 py-3 text-slate-700">Work RVU</td>
                      <td className="px-4 py-3 text-slate-600">Provider time, skill, mental effort, judgment</td>
                      <td className="px-4 py-3"><span className="text-green-600 font-semibold">This calculates your pay.</span></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 font-semibold text-slate-700">PE RVU</td>
                      <td className="px-4 py-3 text-slate-700">Practice Expense RVU</td>
                      <td className="px-4 py-3 text-slate-600">Office overhead, staff costs, equipment, supplies</td>
                      <td className="px-4 py-3 text-slate-500">Goes to the practice.</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 font-semibold text-slate-700">MP RVU</td>
                      <td className="px-4 py-3 text-slate-700">Malpractice RVU</td>
                      <td className="px-4 py-3 text-slate-600">Malpractice insurance cost component</td>
                      <td className="px-4 py-3 text-slate-400">Goes to malpractice coverage.</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            {/* What is a wRVU */}
            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-3 tracking-tight">What Is a wRVU?</h2>
              <p className="text-slate-600 text-base leading-relaxed mb-4">
                The <strong>work RVU (wRVU)</strong> is the component that reflects <em>your</em> effort as the provider — not facility costs or insurance overhead. It measures:
              </p>
              <ul className="space-y-2 mb-4">
                {[
                  "Time required to perform the service (pre-, intra-, and post-service)",
                  "Technical skill and physical effort",
                  "Mental effort and judgment",
                  "Stress related to patient outcome",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-slate-600 text-base">
                    <CheckCircle size={17} className="text-blue-500 flex-shrink-0 mt-0.5" />
                    {item}
                  </li>
                ))}
              </ul>
              <p className="text-slate-600 text-base leading-relaxed">
                For example, a simple office visit (99213) is assigned <strong>1.30 wRVUs</strong>, while a complex new patient visit (99205) is <strong>3.50 wRVUs</strong> — reflecting the significantly greater time and complexity involved.
              </p>
            </section>

            {/* RVU vs wRVU */}
            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-3 tracking-tight">RVU vs. wRVU — What Providers Are Actually Paid On</h2>
              <p className="text-slate-600 text-base leading-relaxed mb-4">
                While <em>total</em> RVUs are used to calculate CMS reimbursement rates, most employer productivity compensation models are based exclusively on <strong>wRVUs</strong> — because they represent only the provider's work, stripping out the facility and insurance cost components.
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 mb-4">
                <p className="text-sm font-semibold text-blue-800 mb-2">Productivity Pay Formula</p>
                <p className="text-2xl font-extrabold text-blue-700">wRVUs × $/wRVU (Conversion Factor) = Estimated Earnings</p>
                <p className="text-xs text-blue-600 mt-2">Example: 500 wRVUs × $55/wRVU = $27,500 in productivity earnings</p>
              </div>
              <p className="text-slate-600 text-base leading-relaxed">
                <p className="text-slate-600 text-base leading-relaxed">
                <strong className="text-slate-800">Your employer sets the Conversion Factor (CF)</strong> — <strong className="text-slate-700">This should be reflected in your contract.</strong>
              </p>
              </p>
            </section>

            {/* Physicians vs NPs/PAs */}
            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-3 tracking-tight">Physicians vs. NPs &amp; PAs — How wRVUs Apply to You</h2>
              <p className="text-slate-600 text-base leading-relaxed mb-5">
                wRVU-based productivity models apply to all provider types, but there are key differences in how wRVUs are credited and billed:
              </p>
              <div className="grid md:grid-cols-2 gap-5">
                <div className="rounded-xl border border-slate-200 bg-white p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                    <h3 className="font-bold text-slate-900 text-base">Physicians (MD/DO)</h3>
                  </div>
                  <ul className="space-y-2">
                    {[
                      "Receive 100% wRVU credit for services billed under their NPI",
                      "Medicare reimburses at 100% of the fee schedule",
                      "Productivity comp typically tied directly to wRVUs generated",
                      "Higher baseline conversion factors in most specialties",
                    ].map((t) => <li key={t} className="text-sm text-slate-600 flex items-start gap-2"><CheckCircle size={14} className="text-green-500 flex-shrink-0 mt-0.5" />{t}</li>)}
                  </ul>
                </div>
                <div className="rounded-xl border border-slate-200 bg-white p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-2 h-2 rounded-full bg-purple-500"></div>
                    <h3 className="font-bold text-slate-900 text-base">NPs &amp; PAs</h3>
                  </div>
                  <ul className="space-y-2">
                    {[
                      "Medicare reimburses NP/PA services at 85% of physician fee schedule",
                      "85% is a payment reduction, not a wRVU value",
                      "wRVU credit depends on how you bill (incident-to vs. independent) — and is ultimately set by your employer contract",
                      "Your CONTRACT determines your wRVU credit and conversion factor — always negotiate",
                    ].map((t) => <li key={t} className="text-sm text-slate-600 flex items-start gap-2"><CheckCircle size={14} className="text-purple-500 flex-shrink-0 mt-0.5" />{t}</li>)}
                  </ul>
                </div>
              </div>
              <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
                <Info size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-amber-800">
                  <strong>Key takeaway for NPs/PAs:</strong> The 85% Medicare rate applies to what your employer <em>gets paid</em> by Medicare — but many employers still credit you 100% of wRVUs in their internal tracking system. Always clarify this in your contract negotiations.
                </p>
              </div>
            </section>

            {/* CPT Reference Table */}
            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-3 tracking-tight">Common CPT Codes &amp; Their wRVU Values</h2>
              <p className="text-slate-600 text-base leading-relaxed mb-5">
                Reference wRVU values for the most common E/M and procedure codes across clinical settings. Values reflect the CMS Medicare Physician Fee Schedule.
              </p>

              {/* Tab selector */}
              <div className="flex flex-wrap gap-2 mb-4">
                {Object.keys(CPT_REFERENCE).map((cat) => (
                  <button
                    key={cat}
                    onClick={() => setActiveTab(cat)}
                    className={`text-xs font-semibold px-4 py-2 rounded-full border transition-all ${
                      activeTab === cat
                        ? "bg-blue-600 text-white border-blue-600"
                        : "bg-white text-slate-600 border-slate-200 hover:border-blue-300"
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>

              <div className="overflow-x-auto rounded-xl border border-slate-200">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="text-left px-3 py-3 font-semibold text-slate-700 w-20">CPT</th>
                      <th className="text-left px-3 py-3 font-semibold text-slate-700">Description</th>
                      <th className="text-right px-3 py-3 font-semibold text-slate-700">wRVU</th>
                      <th className="text-right px-3 py-3 font-semibold text-slate-700 whitespace-nowrap">@ $30/wRVU<span className="block text-xs font-normal text-slate-400">(ex. NP/PA)</span></th>
                      <th className="text-right px-3 py-3 font-semibold text-slate-700 whitespace-nowrap">@ $50/wRVU<span className="block text-xs font-normal text-slate-400">(ex. Physician)</span></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {CPT_REFERENCE[activeTab].map((row) => (
                      <tr key={row.code} className="hover:bg-slate-50 transition-colors">
                        <td className="px-3 py-3 font-bold text-blue-700">{row.code}</td>
                        <td className="px-3 py-3 text-slate-700 text-xs leading-snug">{row.desc}</td>
                        <td className="px-3 py-3 text-right font-semibold text-green-700">{row.wrvu}</td>
                        <td className="px-3 py-3 text-right text-purple-600 font-medium">${(row.wrvu * 30).toFixed(2)}</td>
                        <td className="px-3 py-3 text-right text-blue-600 font-medium">${(row.wrvu * 50).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-slate-400 mt-2">* wRVU values based on CMS Medicare Physician Fee Schedule. Actual values may vary by payer and year.</p>
            </section>

            {/* How productivity pay works */}
            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-3 tracking-tight">How Productivity-Based Pay Is Calculated</h2>
              <p className="text-slate-600 text-base leading-relaxed mb-4">
                Most productivity compensation models work one of two ways:
              </p>
              <div className="space-y-4">
                <div className="bg-white rounded-xl border border-slate-200 p-5">
                  <h3 className="font-bold text-slate-900 mb-2">1. Pure wRVU Model</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">Every wRVU you generate earns a set dollar amount. Example: You generate 4,200 wRVUs in a year at a conversion factor of $52/wRVU = <strong>$218,400 in productivity earnings</strong>. This may be your total compensation or a component on top of a base salary.</p>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 p-5">
                  <h3 className="font-bold text-slate-900 mb-2">2. Base Salary + wRVU Bonus</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">You receive a guaranteed base salary, then earn bonus dollars for every wRVU generated above a threshold (often MGMA* median). This protects income during ramp-up periods while incentivizing high productivity.</p>
                  <p className="text-xs text-slate-400 mt-3">* MGMA: Medical Group Management Association</p>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 p-5">
                  <h3 className="font-bold text-slate-900 mb-3">MGMA Conversion Factor Benchmarks (Approximate)</h3>
                  <div className="flex gap-2 mb-3">
                    {["Physician", "NP/PA"].map((t) => (
                      <button
                        key={t}
                        onClick={() => setMgmaTab(t)}
                        className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-all ${
                          mgmaTab === t
                            ? t === "Physician" ? "bg-blue-600 text-white border-blue-600" : "bg-purple-600 text-white border-purple-600"
                            : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
                        }`}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {(mgmaTab === "Physician" ? [
                      ["Family Medicine", "$48–$55"],
                      ["Internal Medicine", "$48–$58"],
                      ["Emergency Medicine", "$62–$75"],
                      ["Urgent Care", "$42–$55"],
                      ["Hospital Medicine", "$52–$62"],
                      ["General Surgery", "$55–$68"],
                    ] : [
                      ["Family Medicine", "$32–$42"],
                      ["Internal Medicine", "$32–$42"],
                      ["Emergency Medicine", "$40–$52"],
                      ["Urgent Care", "$30–$42"],
                      ["Hospital Medicine", "$38–$48"],
                      ["General Surgery", "$35–$45"],
                    ]).map(([spec, cf]) => (
                      <div key={spec} className="flex justify-between items-center bg-slate-50 rounded-lg px-3 py-2">
                        <span className="text-xs font-medium text-slate-700">{spec}</span>
                        <span className={`text-xs font-bold ${mgmaTab === "Physician" ? "text-blue-700" : "text-purple-700"}`}>{cf}/wRVU</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-slate-400 mt-3">* Approximate MGMA benchmarks. Actual rates vary by market, employer, and contract.</p>
                </div>
              </div>
            </section>

            {/* ── CONVERSION CTA (inline) ───────────────────────────────────── */}
            <section>
              <div
                className="rounded-2xl p-8 text-center"
                style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)" }}
              >
                <h2 className="text-2xl font-extrabold text-white mb-4 tracking-tight">
                  Want Automatic RVU Tracking?
                </h2>
                <div className="space-y-2 mb-6">
                  <p className="text-slate-300 text-base">Stop calculating manually — ONE CLICK is all it takes.</p>
                  <p className="text-slate-300 text-base">Track your productivity automatically, every shift.</p>
                  <p className="text-white font-semibold text-base">Earn what you're worth. The Fastest RVU Tracker. <span className="text-blue-400">PERIOD.</span></p>
                </div>
                <a
                  href={`${APP_URL}/register`}
                  data-testid="inline-cta-register"
                  className="inline-flex items-center gap-2 text-base font-bold text-white rounded-full px-8 py-3.5 transition-all hover:-translate-y-px hover:shadow-xl"
                  style={{ background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)", boxShadow: "0 4px 20px rgba(37,99,235,0.40)" }}
                >
                  Try Doc RVU Tracker
                </a>
              </div>
            </section>

          </div>

          {/* ── RIGHT: Sticky Calculator ─────────────────────────────────────── */}
          <div className="w-full lg:w-2/5 calc-sticky" style={{ position: "sticky", top: "88px", maxHeight: "calc(100vh - 100px)", overflowY: "auto", alignSelf: "flex-start" }}>
            <div className="bg-white rounded-2xl border border-slate-200 shadow-xl overflow-hidden">

              {/* Calculator header */}
              <div className="px-6 py-5 border-b border-slate-100" style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)" }}>
                <div className="flex items-center gap-3">
                  <div className="bg-blue-500/20 rounded-xl p-2">
                    <Calculator size={20} className="text-blue-400" />
                  </div>
                  <div>
                    <h2 className="text-white font-extrabold text-xl">The wRVU Calculator</h2>
                    <p className="text-slate-400 text-xs">Enter CPT codes or keywords</p>
                  </div>
                </div>
              </div>

              {/* Column headers */}
              <div className="grid grid-cols-12 gap-2 px-5 pt-4 pb-2">
                <div className="col-span-4 text-xs font-semibold text-slate-500 uppercase tracking-wide">CPT Code</div>
                <div className="col-span-3 text-xs font-semibold text-slate-500 uppercase tracking-wide"># Visits</div>
                <div className="col-span-2 text-xs font-semibold text-slate-500 uppercase tracking-wide text-center">wRVU ea.</div>
                <div className="col-span-2 text-xs font-semibold text-slate-500 uppercase tracking-wide text-center">Total</div>
                <div className="col-span-1"></div>
              </div>

              {/* Rows */}
              <div className="px-5 pb-3 space-y-2">
                {rows.map((row, i) => (
                  <CptRow
                    key={i}
                    row={row}
                    index={i}
                    onUpdate={updateRow}
                    onRemove={removeRow}
                    canRemove={rows.length > 1}
                  />
                ))}
              </div>

              {/* Add row */}
              {rows.length < 12 && (
                <div className="px-5 pb-4">
                  <button
                    onClick={addRow}
                    className="w-full flex items-center justify-center gap-2 text-sm font-medium text-blue-600 border border-blue-200 border-dashed rounded-lg py-2 hover:bg-blue-50 transition-colors"
                  >
                    <Plus size={14} /> Add CPT Code
                  </button>
                </div>
              )}

              {/* Conversion factor */}
              <div className="mx-5 mb-4 bg-slate-50 rounded-xl border border-slate-200 px-4 py-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-semibold text-slate-700">$/wRVU Conversion Factor</p>
                    <p className="text-xs text-slate-400">Your employer's rate (MGMA median ≈ $52)</p>
                  </div>
                  <div className="flex items-center gap-1.5 bg-white border border-slate-300 rounded-lg px-3 py-1.5">
                    <span className="text-slate-500 text-sm font-semibold">$</span>
                    <input
                      type="number"
                      value={conversionFactor}
                      onChange={(e) => setConversionFactor(e.target.value)}
                      className="w-14 text-sm font-bold text-slate-900 focus:outline-none bg-transparent"
                      min="0"
                      max="200"
                    />
                  </div>
                </div>
              </div>

              {/* Results */}
              <div className="mx-5 mb-5 rounded-xl overflow-hidden border border-blue-200">
                <div className="bg-blue-600 px-4 py-3 flex justify-between items-center">
                  <span className="text-white font-bold text-sm">Total wRVUs</span>
                  <span className="text-white font-extrabold text-xl">{totals.totalWRVU}</span>
                </div>
                <div className="bg-green-600 px-4 py-3 flex justify-between items-center">
                  <span className="text-white font-bold text-sm">Estimated Earnings</span>
                  <span className="text-white font-extrabold text-xl">${totals.earnings}</span>
                </div>
              </div>

              {/* Mini CTA */}
              <div className="mx-5 mb-5 bg-gradient-to-br from-slate-50 to-blue-50 rounded-xl border border-blue-100 p-4 text-center">
                <p className="text-xs font-semibold text-slate-700 mb-1">Want to track this automatically every shift?</p>
                <a
                  href={`${APP_URL}/register`}
                  className="inline-flex items-center gap-1.5 text-xs font-bold text-white rounded-full px-4 py-2 mt-1 transition-all hover:-translate-y-px hover:shadow-md"
                  style={{ background: "linear-gradient(135deg, #2563eb, #1d4ed8)" }}
                >
                  Start Free Trial <ArrowRight size={12} />
                </a>
              </div>

            </div>
          </div>

        </div>
      </div>

      {/* ── CONVERSION CTA ──────────────────────────────────────────────────── */}
      <section className="py-20 px-4" style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)" }}>
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-blue-500/20 border border-blue-500/30 rounded-full px-4 py-1.5 mb-6">
            <Zap size={13} className="text-blue-400" />
            <span className="text-blue-300 text-xs font-semibold uppercase tracking-widest">Ready to go further?</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-4 tracking-tight">
            Track Your wRVUs Every Shift —{" "}
            <span style={{ background: "linear-gradient(135deg, #2563eb 0%, #0ea5e9 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
              One Click at a Time.
            </span>
          </h2>
          <p className="text-slate-300 text-lg mb-8 max-w-2xl mx-auto">
            Calculating wRVUs manually after every shift is time-consuming and error-prone. Doc RVU Tracker does it in real time — one tap per patient, while you're still in the room.
          </p>

          <div className="grid sm:grid-cols-2 gap-3 max-w-xl mx-auto mb-10 text-left">
            {[
              "Log each patient visit with one tap — no typing",
              "wRVUs and earnings calculated instantly in real time",
              "Never miss a visit or lose track of a procedure again",
              "Full shift history, monthly calendar, and CSV export",
              "Built-in CPT lookup with 11,000+ codes and wRVU values",
              "7-day free trial to the full suite of tracker features",
            ].map((t) => (
              <div key={t} className="flex items-start gap-2.5">
                <CheckCircle size={16} className="text-green-400 flex-shrink-0 mt-0.5" />
                <span className="text-slate-300 text-sm">{t}</span>
              </div>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={`${APP_URL}/register`}
              className="flex items-center justify-center gap-2 text-base font-bold text-white rounded-full px-8 py-3.5 transition-all hover:-translate-y-px hover:shadow-xl"
              style={{ background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)", boxShadow: "0 4px 20px rgba(37,99,235,0.40)" }}
            >
              Start Free 7-Day Trial <ArrowRight size={16} />
            </a>
            <a
              href="/#pricing"
              className="flex items-center justify-center gap-2 text-base font-semibold text-slate-300 border border-slate-600 rounded-full px-8 py-3.5 hover:bg-white/5 transition-all"
            >
              View Pricing Plans
            </a>
          </div>

          <p className="text-slate-500 text-xs mt-5">The Fastest RVU Tracker available. PERIOD. — Starting at $9.99/month</p>
        </div>
      </section>

      {/* ── FOOTER ──────────────────────────────────────────────────────────── */}
      <footer className="bg-slate-900 text-slate-400 py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <div className="flex items-center gap-2.5 mb-2">
                <img src="/rvu-logo.png" alt="Doc RVU Tracker" style={{ width: 32, height: 32, borderRadius: "8px", objectFit: "cover", boxShadow: "0 2px 8px rgba(22,163,74,0.30)" }} />
                <span className="text-white font-semibold text-sm">Doc RVU Tracker</span>
              </div>
              <p className="text-xs text-slate-500 max-w-xs">The One-Click tracker built for providers by providers.</p>
              <p className="text-xs text-slate-600">© {new Date().getFullYear()} Doc RVU Tracker. All rights reserved.</p>
            </div>
            <div className="flex flex-col items-center md:items-end gap-1">
              <div className="flex gap-5">
                <a href="/" className="text-sm hover:text-white transition-colors">Home</a>
                <a href={`${APP_URL}/login`} className="text-sm hover:text-white transition-colors">Log In</a>
                <a href={`${APP_URL}/register`} className="text-sm hover:text-white transition-colors">Sign Up</a>
                <a href="/free-rvu-calculator" className="text-sm hover:text-white transition-colors">Free Calculator</a>
              </div>
              <p className="text-sm font-medium text-slate-300">DocRVUTracker@gmail.com</p>
              <p className="text-xs text-slate-400 mt-0.5">For Customer Service and Inquiries, email above</p>
            </div>
          </div>
        </div>
      </footer>

      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(24px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        html { scroll-behavior: smooth; }
        .calc-sticky { scrollbar-width: none; -ms-overflow-style: none; }
        .calc-sticky::-webkit-scrollbar { display: none; }
      `}</style>
    </div>
  );
}
